import { createContext, useContext } from "react";

export const LAUNCH_CASE_ID_STORAGE_KEY = "praetor:launchCaseId";
export const LAUNCH_CASE_NAME_STORAGE_KEY = "praetor:launchCaseName";

export function readLaunchCaseIdFromSession(): string | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const stored = sessionStorage.getItem(LAUNCH_CASE_ID_STORAGE_KEY);
    if (stored) return stored;
    // One-time migration: read old key from before the rename
    const legacy = sessionStorage.getItem("praetor:launchProjectId");
    if (legacy) {
      sessionStorage.setItem(LAUNCH_CASE_ID_STORAGE_KEY, legacy);
      sessionStorage.removeItem("praetor:launchProjectId");
      return legacy;
    }
    return undefined;
  } catch {
    return undefined;
  }
}

/** Returns true when the session was explicitly launched with no case context
 *  (key exists in sessionStorage with value ""). Distinguishes "no case" from
 *  "key never initialized." */
export function wasLaunchedWithoutCase(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return sessionStorage.getItem(LAUNCH_CASE_ID_STORAGE_KEY) === "";
  } catch {
    return false;
  }
}

export function readLaunchCaseNameFromSession(): string | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const stored = sessionStorage.getItem(LAUNCH_CASE_NAME_STORAGE_KEY);
    if (stored) return stored;
    // One-time migration: read old key from before the rename
    const legacy = sessionStorage.getItem("praetor:launchProjectName");
    if (legacy) {
      sessionStorage.setItem(LAUNCH_CASE_NAME_STORAGE_KEY, legacy);
      sessionStorage.removeItem("praetor:launchProjectName");
      return legacy;
    }
    return undefined;
  } catch {
    return undefined;
  }
}

export type SettingsView =
  | "ai"
  | "skills"
  | "skills-detail"
  | "skills-create"
  | "templates"
  | "user"
  | "dev-tools"
  | "admin-skills"
  | "admin-skills-detail"
  | "admin-skills-create"
  | "admin-preferences";

export type RouteData = {
  threadId: string | undefined;
  caseId: string | undefined;
  /** Immutable session-level caseId set on first launch. Survives route changes
   *  and new-thread resets. All new threads inherit this value; thread list
   *  filters by it. `undefined` means "no case context" (launched as default). */
  launchCaseId: string | undefined;
  /** Human-readable name of the launch case. Persisted in sessionStorage
   *  alongside launchCaseId; fetched once-per-session via fetchCaseName()
   *  if not yet cached. Undefined when no case context or fetch still in-flight. */
  launchCaseName: string | undefined;
  docIds: string[] | undefined;
  showThreadList: boolean;
  lastThreadId?: string | undefined;
  loadingThreadFromUrl: boolean;
  /** URL-decoded prompt text from ?prompt= query param or #prompt= URL fragment.
   *  Consumed once on new-thread creation and then cleared so a page refresh
   *  doesn't re-send. */
  prompt?: string | undefined;
  /** Delivery mechanism that provided the prompt — "hash" (URL fragment) or
   *  "query" (query param). Used for diagnostics logging only. */
  promptSource?: "hash" | "query" | undefined;
  /** True when the active route is the dedicated /chats list view. RouteWatcher
   *  skips URL rewriting and AssistantInner swaps the main pane for the list. */
  chatsView?: boolean;
  /** When set, the active route is a settings page. AssistantInner renders the
   *  settings shell instead of Thread/ChatsListPage. */
  settingsView?: SettingsView;
  /** Skill id carried by /settings/skills/:id so the settings shell can render
   *  the correct detail screen. */
  settingsSkillId?: string;
};

export type RouteContextValues = RouteData & {
  onRouteStateChanged: (update: Partial<RouteData>) => void;
};

export const RouteContext = createContext<RouteContextValues | undefined>(
  undefined,
);

export function useRouteContext() {
  const ctx = useContext(RouteContext);
  if (!ctx)
    throw new Error(
      "useRouteContext must be used under <RouteContext.Provider>",
    );
  return ctx;
}
